import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { toJS } from 'mobx'
import { useEffect } from 'react'
import { useRootStore } from 'stores/RootStore'

interface Props {
  invites?: DehydratedInvite[]
  callback: (hydratedInvites: Value[]) => void
}

export type DehydratedUser = { type: 'User'; id: number }
export type DehydratedChannel = { type: 'Channel'; id: number }
export type DehydratedEmail = { type: 'Email'; email: string }
export type DehydratedInvite = DehydratedUser | DehydratedChannel | DehydratedEmail

export const useHydrateInvites = ({ invites = [], callback }: Props) => {
  const rootStore = useRootStore()
  /**
   * This useEffect can be removed altogether if we refactor the recipients
   * selector to accept dehydrated values, which would make many things a lot
   * easier. Though, that is a big change and right now we accept the tradeoff
   * here while we plan that refactoring.
   */
  useEffect(() => {
    const load = async () => {
      const users = invites.filter(({ type }) => type === 'User') as DehydratedUser[]
      const channels = invites.filter(({ type }) => type === 'Channel') as DehydratedChannel[]
      const emails = invites.filter(({ type }) => type === 'Email') as DehydratedEmail[]

      // Wait until all the initial invitees have been loaded to set them as
      // currently select invitees.
      await Promise.allSettled([
        ...users.map(({ id }) => rootStore.users.fetch(id)),
        ...channels.map(({ id }) => rootStore.channels.fetch(id)),
        ...emails.map(() => Promise.resolve()),
      ])

      const hydratedInitialInvites = [
        ...users.map(({ id }) => ({ type: 'User', value: toJS(rootStore.users.get(id)) })),
        ...channels.map(({ id }) => ({ type: 'Channel', value: toJS(rootStore.channels.get(id)) })),
        ...emails.map(({ email }) => ({ type: 'Email', value: email })),
      ].filter(({ value }) => Boolean(value)) as Value[]

      callback(hydratedInitialInvites)
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
